import { useEffect, useRef, useState } from 'react';
import { Application, Graphics, Text, TextStyle } from 'pixi.js';
import { ProvablyFair } from '../../../shared/src/provably-fair';

const BACKEND_HOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'localhost:8787'
  : '9f-game-platform-api.zeyelvis.workers.dev';

const WS_BASE = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${BACKEND_HOST}`;

interface PixiGameProps {
  activeCurrency: string;
  onBetPlaced: (amount: number) => Promise<boolean>;
  onWinClaimed: (multiplier: number) => void;
  balance: number;
  onGameCrashed?: () => void;
}

export default function PixiGame({ activeCurrency, onBetPlaced, onWinClaimed, balance, onGameCrashed }: PixiGameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const wsRef = useRef<WebSocket | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [multiplier, setMultiplier] = useState(1.0);
  const [betAmount, setBetAmount] = useState(1.0);
  
  // 联机状态管理
  const [isMultiplayer, setIsMultiplayer] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [onlineCount, setOnlineCount] = useState(1);
  const [countdown, setCountdown] = useState(0);
  const [gameStatus, setGameStatus] = useState<'waiting' | 'flying' | 'crashed'>('waiting');
  const [hasRenderError, setHasRenderError] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    let isDestroyed = false;
    let app: Application;

    const initPixi = async () => {
      try {
        app = new Application();
        await app.init({
          resizeTo: containerRef.current!,
          backgroundAlpha: 0, 
          preference: 'webgpu', 
          antialias: true
        });

        if (isDestroyed) {
          app.destroy(true);
          return;
        }

        appRef.current = app;
        containerRef.current!.appendChild(app.canvas);

      // 星空背景粒子
      const stars: { x: number; y: number; size: number; speed: number; graphic: Graphics }[] = [];
      const colors = [0x00ffff, 0xbd00ff, 0xff007c];
      
      for (let i = 0; i < 40; i++) {
        const star = new Graphics();
        const size = Math.random() * 2 + 1;
        const color = colors[Math.floor(Math.random() * colors.length)];
        star.circle(0, 0, size);
        star.fill({ color, alpha: Math.random() * 0.7 + 0.3 });
        star.x = Math.random() * app.screen.width;
        star.y = Math.random() * app.screen.height;
        app.stage.addChild(star);
        stars.push({
          x: star.x,
          y: star.y,
          size,
          speed: Math.random() * 1.5 + 0.5,
          graphic: star
        });
      }

      // 绘制飞船
      const rocket = new Graphics();
      rocket.moveTo(0, -20);
      rocket.lineTo(12, 10);
      rocket.lineTo(-12, 10);
      rocket.closePath();
      rocket.fill({ color: 0x00ffff });
      
      const flame = new Graphics();
      flame.moveTo(-6, 12);
      flame.lineTo(0, 25);
      flame.lineTo(6, 12);
      flame.closePath();
      flame.fill({ color: 0xff3b30 });
      rocket.addChild(flame);

      rocket.x = 80;
      rocket.y = app.screen.height - 80;
      app.stage.addChild(rocket);

      // 倍数文本样式
      const style = new TextStyle({
        fontFamily: 'Outfit, sans-serif',
        fontSize: 32,
        fontWeight: 'bold',
        fill: '#00ffff',
        dropShadow: { alpha: 0.5, blur: 6, color: '#000000', distance: 2 }
      });

      const multText = new Text({ text: '1.00x', style });
      multText.x = app.screen.width / 2;
      multText.y = app.screen.height / 3;
      multText.anchor.set(0.5);
      app.stage.addChild(multText);

      // 物理主循环 (Ticker)
      let flameScale = 1.0;
      let gameActive = false;
      
      // 🚀 Dead Reckoning 网络平滑预测与插值状态
      let displayedMultiplier = 1.0;
      let targetMultiplier = 1.0;
      let shakeIntensity = 0;

      app.ticker.add((ticker) => {
        // 星空平移
        stars.forEach(s => {
          s.graphic.x -= s.speed;
          if (s.graphic.x < 0) {
            s.graphic.x = app.screen.width;
            s.graphic.y = Math.random() * app.screen.height;
          }
        });

        // 尾气脉冲
        flameScale = 1.0 + Math.sin(Date.now() * 0.02) * 0.2;
        flame.scale.set(flameScale);

        // 坠机剧烈抖屏特效
        if (shakeIntensity > 0) {
          app.stage.x = (Math.random() - 0.5) * shakeIntensity;
          app.stage.y = (Math.random() - 0.5) * shakeIntensity;
          shakeIntensity *= 0.9;
          if (shakeIntensity < 0.5) {
            shakeIntensity = 0;
            app.stage.x = 0;
            app.stage.y = 0;
          }
        }

        if (gameActive) {
          rocket.x += (app.screen.width / 2 - rocket.x) * 0.02;
          rocket.y += (app.screen.height / 2 - rocket.y) * 0.02;
          rocket.rotation = Math.sin(Date.now() * 0.005) * 0.05;

          // 航向推测：若当前倍率落后于网络目标帧，做缓动平滑逼近；否则在等待间隙中按每毫秒 0.0005 预测递增
          if (displayedMultiplier < targetMultiplier) {
            displayedMultiplier += (targetMultiplier - displayedMultiplier) * 0.1;
          } else {
            displayedMultiplier += ticker.deltaMS * 0.0005;
          }

          multText.text = `${displayedMultiplier.toFixed(2)}x`;
        } else {
          rocket.y = (app.screen.height - 80) + Math.sin(Date.now() * 0.003) * 6;
          rocket.x = 80 + Math.cos(Date.now() * 0.002) * 2;
          rocket.rotation = 0;
        }
      });

      // 挂载控制器 API 供外部控制
      (app as any).startGame = () => {
        gameActive = true;
        rocket.x = 80;
        rocket.y = app.screen.height - 80;
        displayedMultiplier = 1.0;
        targetMultiplier = 1.0;
      };

      (app as any).stopGame = () => {
        gameActive = false;
        return displayedMultiplier;
      };

      (app as any).updateServerTick = (multiplier: number) => {
        targetMultiplier = multiplier;
      };
      
      (app as any).resetMultiplier = () => {
        displayedMultiplier = 1.0;
        targetMultiplier = 1.0;
        multText.text = '1.00x';
      };

      (app as any).crashGame = (crashPoint: number) => {
        gameActive = false;
        displayedMultiplier = crashPoint;
        targetMultiplier = crashPoint;
        multText.text = `💥 ${crashPoint.toFixed(2)}x`;
        shakeIntensity = 15; // 启动 15px 抖屏
      };
      
      (app as any).setGameActive = (active: boolean) => {
        gameActive = active;
      };
      } catch (err) {
        console.warn('[PixiGame] 3D引擎初始化异常，已安全激活上线级 CSS 降级模式:', err);
        setHasRenderError(true);
        appRef.current = {
          startGame: () => {},
          stopGame: () => multiplier,
          updateServerTick: () => {},
          resetMultiplier: () => {},
          crashGame: () => {},
          setGameActive: () => {},
        } as any;
      }
    };

    initPixi();

    return () => {
      isDestroyed = true;
      if (app) {
        try {
          app.destroy(true, { children: true, texture: true });
        } catch (err) {
          console.warn('[PixiGame] 良性销毁异常已安全过滤:', err);
        }
      }
    };
  }, []);

  // ==========================================
  // WebSocket 多人联机状态同步逻辑
  // ==========================================
  useEffect(() => {
    if (!isMultiplayer) {
      if (wsRef.current) wsRef.current.close();
      setWsConnected(false);
      return;
    }

    // 连接 Worker 环境 DO 房间 (带测试用户参量)
    const socket = new WebSocket(`${WS_BASE}/api/room/crash?userId=user_web_client&username=Player_Web`);
    wsRef.current = socket;

    socket.onopen = () => setWsConnected(true);
    socket.onclose = () => setWsConnected(false);
    socket.onerror = () => setWsConnected(false);

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'INIT_STATE' || data.type === 'COUNTDOWN') {
        if (data.onlineCount) setOnlineCount(data.onlineCount);
        if (data.value !== undefined) setCountdown(data.value);
        if (data.roomState) setGameStatus(data.roomState);
      }

      if (data.type === 'GAME_RESET') {
        setGameStatus('waiting');
        setCountdown(data.countdown);
        setMultiplier(1.0);
        if (appRef.current) {
          (appRef.current as any).setGameActive(false);
          (appRef.current as any).resetMultiplier();
        }
      }

      if (data.type === 'GAME_STARTED') {
        setGameStatus('flying');
        setMultiplier(1.0);
        setIsPlaying(true);
        if (appRef.current) (appRef.current as any).startGame();
      }

      if (data.type === 'TICK') {
        setMultiplier(data.multiplier);
        if (appRef.current) {
          (appRef.current as any).updateServerTick(data.multiplier);
        }
      }

      if (data.type === 'GAME_CRASHED') {
        setGameStatus('crashed');
        setIsPlaying(false);
        if (appRef.current) {
          (appRef.current as any).crashGame(data.crashPoint);
        }
        onGameCrashed?.();
        alert(`💥 全网多人爆炸！崩溃点为: ${data.crashPoint.toFixed(2)}x \n揭示服务端种子(ServerSeed): \n${data.revealedPlainSeed}`);
      }
      
      if (data.type === 'CASHOUT_SUCCESS') {
        setIsPlaying(false);
        onWinClaimed(data.multiplier);
        alert(`🎉 联机逃跑成功！倍数: ${data.multiplier}x, 赢得: ${data.profit}！`);
      }
    };

    return () => socket.close();
  }, [isMultiplayer]);

  // ==========================================
  // 下注与逃跑操作
  // ==========================================
  const handleStart = async () => {
    if (balance < betAmount) {
      alert('余额不足！');
      return;
    }

    if (isMultiplayer) {
      // 联机模式下发送下注指令给 DO
      if (wsRef.current && wsConnected) {
        const success = await onBetPlaced(betAmount);
        if (success) {
          wsRef.current.send(JSON.stringify({ type: 'PLACE_BET', amount: betAmount }));
          setIsPlaying(true);
        }
      } else {
        alert('联机服务未就绪，请先检查后端模拟器运行状态！');
      }
    } else {
      // 单机公平性模拟模式
      const success = await onBetPlaced(betAmount);
      if (success && appRef.current) {
        setIsPlaying(true);
        setMultiplier(1.0);
        (appRef.current as any).startGame();

        const serverSeed = `server_seed_${Math.random().toString(36).substring(2)}`;
        const clientSeed = 'player_client_seed_2026';
        const nonce = Math.floor(Math.random() * 1000) + 1;

        const crashMultiplier = await ProvablyFair.calculateCrashOutcome(serverSeed, clientSeed, nonce);
        
        // 由于本地每毫秒自增 0.0005，计算出本地崩溃运行时间
        const runDuration = (crashMultiplier - 1.0) / 0.0005;

        // 本地 Ticker 驱动乘数变动
        let localMult = 1.0;
        const interval = setInterval(() => {
          localMult += 0.125;
          setMultiplier(localMult);
          if (appRef.current) (appRef.current as any).updateServerTick(localMult);
        }, 250);

        setTimeout(() => {
          clearInterval(interval);
          setIsPlaying(prev => {
            if (prev) {
              alert(`💥 Boom! 在 ${crashMultiplier.toFixed(2)}x 瞬间 Crash 掉了！`);
              onWinClaimed(0);
              if (appRef.current) (appRef.current as any).crashGame(crashMultiplier);
              onGameCrashed?.();
            }
            return false;
          });
        }, Math.max(50, runDuration));
      }
    }
  };

  const handleCashout = () => {
    if (isMultiplayer) {
      if (wsRef.current && wsConnected) {
        wsRef.current.send(JSON.stringify({ type: 'CASHOUT' }));
      }
    } else {
      if (!isPlaying || !appRef.current) return;
      const finalMult = (appRef.current as any).stopGame();
      onWinClaimed(finalMult);
      setIsPlaying(false);
      alert(`🎉 成功逃跑！获得 ${finalMult.toFixed(2)}x 收益！`);
    }
  };

  return (
    <div className="glass-card" style={{ padding: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 12px' }}>
        <h3 style={{ fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span className="pulsing-dot" style={{ background: wsConnected ? 'var(--color-primary)' : '#888' }}></span>
          9F Crash 2D (PixiJS v8 + WebGPU)
        </h3>
        
        {/* 联机选择器 */}
        <button 
          onClick={() => setIsMultiplayer(!isMultiplayer)}
          style={{
            background: 'none', border: '1px solid var(--border-glass-active)',
            borderRadius: '20px', padding: '4px 10px', color: 'var(--text-main)',
            fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-primary)'
          }}
        >
          {isMultiplayer ? `🟢 联机大厅 (${onlineCount}人)` : '🔴 本地公平模式'}
        </button>
      </div>
      
      <div className="game-frame" ref={containerRef} style={{ position: 'relative' }}>
        {/* 3D WebGPU 渲染引擎异常时的优质 CSS 安全降级方案 */}
        {hasRenderError && (
          <div style={{ 
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', 
            background: 'radial-gradient(circle at center, #111b27 0%, #07080c 100%)', 
            overflow: 'hidden', borderRadius: '8px'
          }}>
            <div style={{ 
              fontSize: '48px', 
              animation: isPlaying ? 'bounce 0.4s infinite alternate' : 'bounce 2s infinite alternate',
              textShadow: '0 0 20px var(--color-primary)',
              zIndex: 2
            }}>
              🚀
            </div>
            
            <div style={{ 
              fontSize: '32px', fontWeight: 'bold', color: 'var(--color-primary)', 
              marginTop: '12px', textShadow: '0 0 10px rgba(0,255,255,0.5)', fontFamily: 'monospace',
              zIndex: 2
            }}>
              {isPlaying ? `${multiplier.toFixed(2)}x` : '1.00x'}
            </div>
            
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '8px', zIndex: 2 }}>
              已安全挂载 CSS 高容错备用引擎
            </div>
          </div>
        )}

        {/* 联机状态倒计时提示覆盖层 */}
        {isMultiplayer && gameStatus === 'waiting' && (
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', borderRadius: '8px' }}>
            <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>等待下注中...</div>
            <div style={{ fontSize: '36px', fontWeight: 'bold', color: 'var(--color-primary)', fontFamily: 'monospace' }}>
              {countdown.toFixed(1)}s
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '12px', padding: '0 12px 12px' }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>投注额 ({activeCurrency.toUpperCase()})</label>
          <input
            type="number"
            disabled={isPlaying}
            value={betAmount}
            onChange={(e) => setBetAmount(Number(e.target.value))}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--border-glass)',
              color: 'var(--text-main)',
              padding: '10px',
              borderRadius: '8px',
              marginTop: '4px',
              fontFamily: 'var(--font-primary)'
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', flex: 1 }}>
          {!isPlaying ? (
            <button 
              className="btn-neon" 
              onClick={handleStart} 
              disabled={isMultiplayer && gameStatus !== 'waiting'}
              style={{ width: '100%', padding: '12px', opacity: isMultiplayer && gameStatus !== 'waiting' ? 0.5 : 1 }}
            >
              {isMultiplayer && gameStatus !== 'waiting' ? '起飞中...' : '下 注'}
            </button>
          ) : (
            <button 
              className="btn-neon" 
              onClick={handleCashout} 
              style={{ width: '100%', padding: '12px', background: 'linear-gradient(90deg, var(--color-accent), var(--color-secondary))' }}
            >
              逃 跑 ({multiplier.toFixed(2)}x)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
